/**
 * Timezone Utilities
 * Backend stores ALL timestamps as naive UTC (no Z suffix).
 * Agent sends UTC timestamps → normalizer strips tz → stored as naive UTC.
 * Frontend must treat all naive timestamps as UTC, then convert to browser local for display.
 */

/**
 * Get the browser's local timezone
 * @returns {string} The browser's timezone (e.g., 'Asia/Kolkata')
 */
export const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    return 'UTC';
  }
};

/**
 * Parse a timestamp from the backend as UTC.
 * If the string has no timezone suffix (Z or ±offset), append 'Z' to force UTC interpretation.
 * @param {string|Date} dateTime - UTC datetime from backend
 * @returns {Date} Parsed Date object (in JS internal UTC representation)
 */
export const parseUTCTimestamp = (dateTime) => {
  if (!dateTime) return null;
  if (dateTime instanceof Date) return dateTime;

  const ts = String(dateTime);
  // Already has timezone info → parse as-is
  if (ts.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(ts)) return new Date(ts);
  // Naive timestamp from backend → treat as UTC
  return new Date(ts + 'Z');
};

/**
 * Format a date/time string for user's timezone.
 * Treats all incoming timestamps as UTC, converts to browser local for display.
 * @param {string|Date} dateTime - UTC datetime from backend
 * @param {string} format - Format type: 'full', 'date', 'time', 'monthYear', 'compact'
 * @param {string} userTimezone - User's timezone (default: browser's local timezone)
 * @returns {string} Formatted datetime in user's local timezone
 */
export const formatToUserTimezone = (dateTime, format = 'full', userTimezone = null) => {
  if (!dateTime) return 'N/A';

  try {
    const date = parseUTCTimestamp(dateTime);
    if (!date || isNaN(date.getTime())) return dateTime;

    // Use configured timezone (from shift_templates/settings), fallback to browser local
    const tz = userTimezone || getBrowserTimezone();
    const tzOpt = { timeZone: tz };

    if (format === 'date') {
      return date.toLocaleDateString('en-US', {
        ...tzOpt, year: 'numeric', month: 'short', day: 'numeric'
      });
    } else if (format === 'time') {
      return date.toLocaleTimeString('en-US', {
        ...tzOpt, hour: '2-digit', minute: '2-digit', hour12: true
      });
    } else if (format === 'monthYear') {
      return date.toLocaleDateString('en-US', {
        ...tzOpt, year: 'numeric', month: 'long'
      });
    } else if (format === 'compact') {
      return date.toLocaleDateString('en-US', {
        ...tzOpt, month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    }

    return date.toLocaleString('en-US', {
      ...tzOpt, year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateTime;
  }
};

/**
 * Legacy function for backward compatibility - uses IST
 * @deprecated Use formatToUserTimezone instead
 */
export const formatIST = (dateTime, format = 'full') => {
  return formatToUserTimezone(dateTime, format, 'Asia/Kolkata');
};

/**
 * Format an event timestamp that may originate from two different sources with
 * different conventions:
 * - Go agent: real UTC, always suffixed with 'Z' (or a +/-offset) → convert to
 *   the user's timezone, same as formatToUserTimezone/parseUTCTimestamp.
 * - Swift Launcher (macOS permission wizard): naive local wall-clock time, no
 *   'Z'/offset suffix → the string IS already the local time the event
 *   happened at, so it must be rendered as-is with NO timezone conversion.
 *   (parseUTCTimestamp would incorrectly treat it as UTC and shift it again.)
 *
 * Use this for displays that mix events from both origins (e.g. the Device
 * Detail "Permission Timeline"). Do not use for fields that are always
 * UTC-stored (use formatToUserTimezone/formatDateTime for those instead).
 * @param {string|Date} dateTime - Event timestamp (UTC+Z or naive local string)
 * @param {string} userTimezone - User's timezone, used only for the UTC branch
 * @returns {string} Formatted datetime, e.g. "Sep 8, 2026, 11:06 AM"
 */
export const formatAgentEventTimestamp = (dateTime, userTimezone = null) => {
  if (!dateTime) return 'N/A';

  try {
    if (dateTime instanceof Date) {
      return formatToUserTimezone(dateTime, 'full', userTimezone);
    }

    const ts = String(dateTime);
    const hasTzSuffix = ts.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(ts);

    if (hasTzSuffix) {
      // Real UTC from the Go agent → convert to the user's local timezone.
      return formatToUserTimezone(ts, 'full', userTimezone);
    }

    // Naive local wall-clock time from the Swift Launcher → parse the literal
    // date/time components and format them without any timezone shifting.
    const match = ts.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (!match) {
      // Unrecognized format — fall back to best-effort UTC parsing.
      return formatToUserTimezone(ts, 'full', userTimezone);
    }

    const [, year, month, day, hour, minute] = match;
    const literalDate = new Date(
      Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute)
    );
    if (isNaN(literalDate.getTime())) return ts;

    return literalDate.toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  } catch (error) {
    console.error('Error formatting agent event timestamp:', error);
    return dateTime;
  }
};

/**
 * Get relative time (e.g., "5 minutes ago")
 * @param {string|Date} dateTime - UTC datetime from backend
 * @returns {string} Relative time string
 */
export const getRelativeTime = (dateTime) => {
  if (!dateTime) return 'N/A';

  try {
    const date = parseUTCTimestamp(dateTime);
    if (!date || isNaN(date.getTime())) return dateTime;

    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 0) return 'just now';
    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHour < 24) return `${diffHour}h ago`;
    return `${diffDay}d ago`;
  } catch (error) {
    return dateTime;
  }
};

/**
 * Format any date to the user's local system time
 * @param {string|Date} dateTime - UTC datetime from backend
 * @returns {string} Formatted datetime in browser local timezone
 */
export const formatLocalTime = (dateTime) => {
  if (!dateTime) return 'N/A';
  try {
    const date = parseUTCTimestamp(dateTime);
    if (!date || isNaN(date.getTime())) return dateTime;
    return date.toLocaleString();
  } catch (error) {
    return dateTime;
  }
};

/**
 * Format date only
 * @param {string|Date} dateTime - UTC datetime from backend
 * @returns {string} Formatted date in browser local timezone
 */
export const formatLocalDate = (dateTime) => {
  if (!dateTime) return 'N/A';
  try {
    const date = parseUTCTimestamp(dateTime);
    if (!date || isNaN(date.getTime())) return dateTime;
    return date.toLocaleDateString();
  } catch (error) {
    return dateTime;
  }
};

/**
 * Format time only
 * @param {string|Date} dateTime - UTC datetime from backend
 * @returns {string} Formatted time in browser local timezone
 */
export const formatLocalTimeOnly = (dateTime) => {
  if (!dateTime) return 'N/A';
  try {
    const date = parseUTCTimestamp(dateTime);
    if (!date || isNaN(date.getTime())) return dateTime;
    return date.toLocaleTimeString();
  } catch (error) {
    return dateTime;
  }
};

/**
 * Fallback IANA → abbreviation map for zones where the ICU/CLDR data behind
 * Intl.DateTimeFormat has no unique short code and instead falls back to a
 * "GMT+X:XX" offset string (e.g. India, the Gulf). Only zones known to hit
 * that fallback need an entry here — zones with a real short code (US/EU/etc,
 * which also correctly flip between standard/daylight variants like EST/EDT)
 * are resolved dynamically via Intl instead of being hardcoded.
 */
const TZ_ABBR_FALLBACK = {
  'Asia/Kolkata': 'IST', 'Asia/Dubai': 'GST', 'Asia/Riyadh': 'AST',
  'Asia/Singapore': 'SGT', 'Asia/Shanghai': 'CST', 'Asia/Tokyo': 'JST',
  'Asia/Seoul': 'KST', 'Asia/Manila': 'PHT', 'Asia/Bangkok': 'ICT',
  'Asia/Jakarta': 'WIB', 'Asia/Karachi': 'PKT', 'Asia/Dhaka': 'BST',
  'Asia/Colombo': 'SLST', 'Asia/Kuala_Lumpur': 'MYT', 'Asia/Hong_Kong': 'HKT',
  'Asia/Taipei': 'CST', 'Asia/Ho_Chi_Minh': 'ICT',
  'Pacific/Honolulu': 'HST', 'America/Phoenix': 'MST',
  'America/Sao_Paulo': 'BRT', 'America/Buenos_Aires': 'ART',
  'America/Argentina/Buenos_Aires': 'ART', 'America/Bogota': 'COT',
  'America/Lima': 'PET', 'Europe/Moscow': 'MSK', 'Europe/Istanbul': 'TRT',
  'Africa/Cairo': 'EET', 'Africa/Lagos': 'WAT', 'Africa/Johannesburg': 'SAST',
  'Africa/Nairobi': 'EAT', 'Australia/Perth': 'AWST', 'Pacific/Fiji': 'FJT',
  'UTC': 'UTC',
};

/**
 * Resolve a short abbreviation for an IANA timezone (e.g. "Asia/Kolkata" → "IST",
 * "America/New_York" → "EST"/"EDT" depending on DST). Prefers the browser's
 * Intl data (so DST-aware zones stay correct), falling back to a static map
 * for zones ICU has no unique abbreviation for and would otherwise render as
 * "GMT+5:30".
 * @param {string} tz - IANA timezone identifier
 * @returns {string} Short abbreviation, or the raw tz id if unresolvable
 */
export const getTimezoneAbbr = (tz) => {
  if (!tz) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(new Date());
    const abbr = parts.find((p) => p.type === 'timeZoneName')?.value;
    if (abbr && !/^GMT/.test(abbr)) return abbr;
  } catch {
    // fall through to static map
  }
  return TZ_ABBR_FALLBACK[tz] || tz;
};

/**
 * Full display label for an IANA timezone, combining the short abbreviation
 * with the IANA name so the exact zone is always unambiguous in the UI,
 * e.g. "IST - Asia/Kolkata", "EDT - America/New_York".
 * @param {string} tz - IANA timezone identifier
 * @returns {string} "{ABBR} - {IANA}" label, or just the tz id if unresolvable
 */
export const getTimezoneLabel = (tz) => {
  if (!tz) return '';
  const abbr = getTimezoneAbbr(tz);
  if (!abbr || abbr === tz) return tz;
  return `${abbr} - ${tz}`;
};

/**
 * Offset (in ms) of a timezone from UTC at a given instant.
 * @param {Date} date - Instant to evaluate the offset at
 * @param {string} timeZone - IANA timezone
 * @returns {number} Offset in milliseconds (positive east of UTC)
 */
export const getTimezoneOffsetMs = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  }).formatToParts(date);

  const p = {};
  for (const { type, value } of parts) p[type] = value;

  const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second);
  return asUTC - Math.floor(date.getTime() / 1000) * 1000;
};

/**
 * Closed window covering the complete previous calendar day in `timeZone`,
 * expressed as UTC ISO instants so the backend receives an unambiguous range.
 * @param {string} timeZone - Organization timezone
 * @returns {{ start: string, end: string, date: string }}
 */
export const getYesterdayRangeUTC = (timeZone) => {
  const tz = timeZone || getBrowserTimezone();
  const dayStr = new Intl.DateTimeFormat('en-CA', { timeZone: tz })
    .format(new Date(Date.now() - 24 * 60 * 60 * 1000));

  const [y, m, d] = dayStr.split('-').map(Number);
  const wallStart = Date.UTC(y, m - 1, d, 0, 0, 0, 0);
  const wallEnd = Date.UTC(y, m - 1, d, 23, 59, 59, 999);
  const offset = getTimezoneOffsetMs(new Date(wallStart), tz);

  return {
    start: new Date(wallStart - offset).toISOString(),
    end: new Date(wallEnd - offset).toISOString(),
    date: dayStr
  };
};
